﻿import { Button, Snackbar } from "@material-ui/core";
import { Icon, IIconProps } from "../Components";
import React, {
    forwardRef,
    Fragment,
    ReactElement,
    useImperativeHandle,
    useState,
} from "react";
import { SnackbarOrigin } from "@material-ui/core/Snackbar";

interface IAction {
    /**
     * The function to fire when the button is clicked
     */
    onClick: () => void;

    /**
     * The title of the button
     */
    title: string;
}

export interface INotification {
    /**
     * If this object is provided then show a button in the snackbar
     * this object has a text to show on the button and the function to call when the button is clicked
     */
    action?: IAction;

    /**
     * The number of milliseconds to display the notification for, unless closed by the user
     */
    durationMs?: number;

    /**
     * Icon to display for the notification
     */
    icon?: IIconProps;

    /**
     * A string message to display, or custom component to render inside the container
     */
    message: string | React.ReactNode;

    /**
     * Location of the notification
     */
    origin?: SnackbarOrigin;

    /**
     * The severity of the notification to be displayed, determining the background colour of the container.
     */
    severity?: "error" | "warning" | "info" | "success";
}

export interface INotificationQueueItem {
    notification: INotification;
    uid: number;
}

const MAX_CONCURRENT_NOTIFICATIONS = 3;

const NOTIFICATION_URLS = {
    error: `UI/NotificationIcons/notifications-default-failure.svg`,
    info: `UI/NotificationIcons/notifications-default-information.svg`,
    success: `UI/NotificationIcons/notifications-default-success.svg`,
    warning: `UI/NotificationIcons/notifications-default-warning.svg`,
};

let lastUid = 0;

// TODO: remove this lint disable and fix
// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IProps {}

export interface INotificationProviderRef {
    addNotificationToQueue?: (notification: INotification) => void;
}

// TODO: remove this disable and add a display name
// eslint-disable-next-line react/display-name
export const NotificationProvider = forwardRef<
    INotificationProviderRef,
    IProps
>((props, ref): ReactElement => {
    const [notificationQueue, setNotificationQueue] = useState<
            INotificationQueueItem[]
        >([]),
        addNotificationToQueue = (notification: INotification): void => {
            setNotificationQueue((oldArray) => [
                ...oldArray,
                {
                    notification,
                    uid: lastUid++,
                },
            ]);
        };

    useImperativeHandle(ref, () => ({
        addNotificationToQueue(notification: INotification): void {
            addNotificationToQueue(notification);
        },
    }));

    return (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <Fragment>
            {notificationQueue.map((item, index) => {
                const notificationType = item.notification.severity
                        ? item.notification.severity
                        : `info`,
                    origin = {
                        horizontal:
                            item.notification.origin?.horizontal || `center`,
                        vertical: item.notification.origin?.vertical || `top`,
                    };

                return (
                    <Snackbar
                        anchorOrigin={origin}
                        autoHideDuration={
                            item.notification.durationMs !== undefined
                                ? item.notification.durationMs
                                : 5000
                        }
                        key={item.uid}
                        onClose={(event, reason): void => {
                            if (reason === `timeout`) {
                                setNotificationQueue((oldArray) =>
                                    oldArray
                                        .slice(0)
                                        .filter(
                                            (notification) =>
                                                notification.uid !== item.uid,
                                        ),
                                );
                            }
                        }}
                        open={index < MAX_CONCURRENT_NOTIFICATIONS}
                    >
                        <div className={`notification-container`}>
                            {((): JSX.Element => {
                                const iconProps: IIconProps = {
                                    url: `${ROOT_IMAGES_URL}${
                                        item.notification.severity
                                            ? NOTIFICATION_URLS[
                                                  item.notification.severity
                                              ]
                                            : NOTIFICATION_URLS.info
                                    }`,
                                    ...(item.notification.icon || {}),
                                };

                                iconProps.className = `notification-icon ${iconProps.className}`;

                                return (
                                    <div
                                        className={`icon-container ${notificationType}`}
                                    >
                                        <Icon {...iconProps} />
                                    </div>
                                );
                            })()}
                            <div className={`notification`}>
                                {item.notification.message}
                                {item.notification.action && (
                                    <div className={`button-container`}>
                                        <Button
                                            autoFocus
                                            className={`button`}
                                            color={`secondary`}
                                            // eslint-disable-next-line @typescript-eslint/ban-types
                                            onClick={(): void =>
                                                item.notification.action.onClick()
                                            }
                                            variant={`contained`}
                                        >
                                            {item.notification.action.title}
                                        </Button>
                                    </div>
                                )}
                            </div>
                            <Icon
                                className={`dismiss-notification`}
                                onClick={(): void =>
                                    setNotificationQueue((oldArray) =>
                                        oldArray
                                            .slice(0)
                                            .filter(
                                                (notification) =>
                                                    notification.uid !==
                                                    item.uid,
                                            ),
                                    )
                                }
                                url={`${ROOT_IMAGES_URL}UI/NotificationIcons/cross.svg`}
                            />
                        </div>
                    </Snackbar>
                );
            })}
        </Fragment>
    );
});
